import React from 'react';
import type { Currency } from '@hero/schema';
import { formatNumberWithCommas } from '@hero/core';
import { cn } from '@hero/web-lib';
import { CurrencySymbol } from './Price';

const COLOR_CLASSES = {
  green: 'bg-emerald-50 text-emerald-700',
  red: 'bg-red-50 text-red-700',
  amber: 'bg-amber-50 text-amber-700',
  blue: 'bg-blue-50 text-blue-700',
  gray: 'bg-gray-100 text-gray-500',
} as const;

export interface PriceBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  price: number;
  currency: Currency;
  color?: keyof typeof COLOR_CLASSES;
  icon?: React.ReactNode;
  /** Prefix shown before the formatted price (e.g. "+" or "-") */
  prefix?: string;
}

export const PriceBadge = React.forwardRef<HTMLDivElement, PriceBadgeProps>(
  ({ price, currency, color = 'green', icon, prefix, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-semibold',
          COLOR_CLASSES[color],
          className,
        )}
        {...props}>
        {icon}
        <span>
          {prefix}<CurrencySymbol currency={currency} /> {formatNumberWithCommas(Math.abs(price))}
        </span>
      </div>
    );
  },
);

PriceBadge.displayName = 'PriceBadge';
